import React from 'react';
import { motion } from 'framer-motion';

/**
 * GlassCard
 * 
 * Premium glassmorphism card component with hover effects.
 * 
 * Props:
 * - children: Content
 * - className: Additional classes
 * - active: Whether the card is selected/active (adds border/glow)
 * - hoverEffect: Whether to enable scale/lift on hover (default: false)
 * - delay: Animation delay index
 */
const GlassCard = ({
    children,
    className = '',
    active = false,
    hoverEffect = false,
    delay = 0,
    variant = 'default'
}) => {
    const variants = {
        default: `
            bg-white/70 backdrop-blur-xl border 
            ${active ? 'border-purple-500 ring-2 ring-purple-500/20' : 'border-white/50'}
            shadow-xl
        `,
        neon: `
            bg-purple-900/40 backdrop-blur-xl border-purple-500/50 
            neon-border-purple text-white
            ${active ? 'ring-2 ring-purple-400/50' : ''}
        `
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                delay: delay * 0.1,
                duration: 0.5,
                type: "spring",
                stiffness: 100
            }}
            className={`
        relative rounded-3xl p-6 md:p-8 
        ${variants[variant] || variants.default}
        ${hoverEffect && variant === 'default' ? 'hover:shadow-2xl hover:bg-white/80 transition-all duration-300 transform hover:-translate-y-1' : ''}
        ${hoverEffect && variant === 'neon' ? 'hover:shadow-purple-500/30 hover:bg-purple-900/60 transition-all duration-300 transform hover:-translate-y-1' : ''}
        ${className}
      `}
        >
            {/* Internal Shine Effect */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/40 to-transparent pointer-events-none" />

            {/* Content */}
            <div className="relative z-10">
                {children}
            </div>
        </motion.div>
    );
};

export default GlassCard;
